import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import {
  UserCheck, UserX, Clock, UserMinus, Fingerprint,
  Upload, Download, Loader2, Search, X,
  LayoutDashboard, TableProperties, Settings2, TrendingUp,
} from 'lucide-react';
import { YearContext } from '../components/Layout/Layout';
import StatCard from '../components/Cards/StatCard';
import ImportExport from '../components/Common/ImportExport';
import DataTable, { StatusBadge } from '../components/Common/DataTable';
import {
  BarChartComponent, LineChartComponent,
  PieChartComponent, HorizontalBarChart,
} from '../components/Charts/ChartComponents';
import { attendanceAPI } from '../services/api';
import EnhancedDashboard from '../components/Attendance/EnhancedDashboard';
import AttendanceAnalytics from '../components/Attendance/AttendanceAnalytics';
import BiometricSync from './BiometricSync';
import toast from 'react-hot-toast';

// ── Date helpers ──────────────────────────────────────────
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function weekStart() {
  const d = new Date();
  const diff = d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1);
  const m = new Date(d.setDate(diff));
  return `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-${String(m.getDate()).padStart(2, '0')}`;
}
function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(typeof v === 'string' ? v + 'T00:00:00' : v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Config ────────────────────────────────────────────────
const MODULE_TABS = [
  { key: 'Dashboard',  Icon: LayoutDashboard },
  { key: 'Analytics',  Icon: TrendingUp      },
  { key: 'Attendance', Icon: TableProperties  },
  { key: 'Setup',      Icon: Settings2        },
];

const STATUS_CARDS = [
  { key: 'present',      label: 'Present',       Icon: UserCheck,  color: 'green',  statKey: 'present_only' },
  { key: 'absent',       label: 'Absent',        Icon: UserX,      color: 'red',    statKey: 'absent_count' },
  { key: 'late',         label: 'Late Coming',   Icon: Clock,      color: 'yellow', statKey: 'late_count'   },
  { key: 'half_day',     label: 'Half Day',      Icon: UserMinus,  color: 'purple', statKey: 'half_day_count' },
  { key: 'single_punch', label: 'Single Punch',  Icon: Fingerprint,color: 'teal',   statKey: 'single_punch_count' },
];

const DATE_PRESETS = [
  { key: 'today', label: 'Today'      },
  { key: 'week',  label: 'This Week'  },
  { key: 'month', label: 'This Month' },
  { key: 'custom',label: 'Custom'     },
];

const ATT_STATUS_FILTERS = [
  { key: '',              label: 'All'          },
  { key: 'present',      label: 'Present'       },
  { key: 'absent',       label: 'Absent'        },
  { key: 'late',         label: 'Late Coming'   },
  { key: 'half_day',     label: 'Half Day'      },
  { key: 'single_punch', label: 'Single Punch'  },
];

// ══════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════
export default function Attendance() {
  const { yearId, refreshKey } = useContext(YearContext);
  const today = useMemo(todayStr, []);

  // ── Module tab ───────────────────────────────────────────
  const [moduleTab, setModuleTab] = useState('Dashboard');

  // ── Dashboard: today's stats ─────────────────────────────
  const [todayStats,   setTodayStats]   = useState(null);
  const [todayLoading, setTodayLoading] = useState(true);
  const [drillStatus,  setDrillStatus]  = useState(null);
  const [drillRecords, setDrillRecords] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  // ── Dashboard: charts ────────────────────────────────────
  const [teachers,     setTeachers]     = useState({ all: [], top5: [], bottom5: [] });
  const [trend,        setTrend]        = useState([]);
  const [chartsLoading,setChartsLoading]= useState(true);

  // ── Attendance tab state ──────────────────────────────────
  const [attDateMode,     setAttDateMode]     = useState('today');
  const [attCustomStart,  setAttCustomStart]  = useState('');
  const [attCustomEnd,    setAttCustomEnd]    = useState('');
  const [attStatus,       setAttStatus]       = useState('');
  const [attSearchInput,  setAttSearchInput]  = useState('');
  const [attSearch,       setAttSearch]       = useState('');
  const [attRecords,      setAttRecords]      = useState([]);
  const [attLoading,      setAttLoading]      = useState(false);

  // ── Helpers ──────────────────────────────────────────────
  const getDateRange = useCallback((mode, cs, ce) => {
    if (mode === 'today')  return [today, today];
    if (mode === 'week')   return [weekStart(), today];
    if (mode === 'month')  return [monthStart(), today];
    return [cs || today, ce || today];
  }, [today]);

  // ── Load today's stats ───────────────────────────────────
  const loadTodayStats = useCallback(async () => {
    setTodayLoading(true);
    try {
      const r = await attendanceAPI.getStats({ academicYearId: yearId, startDate: today, endDate: today });
      setTodayStats(r.data);
    } catch {}
    finally { setTodayLoading(false); }
  }, [yearId, today]);

  useEffect(() => { loadTodayStats(); }, [loadTodayStats, refreshKey]);

  // ── Load drill-down on stat card click ───────────────────
  useEffect(() => {
    if (!drillStatus) { setDrillRecords([]); return; }
    setDrillLoading(true);
    attendanceAPI.getDailyRecords({
      academicYearId: yearId, startDate: today, endDate: today, status: drillStatus,
    }).then(r => setDrillRecords(r.data || [])).catch(() => {}).finally(() => setDrillLoading(false));
  }, [drillStatus, yearId, today]);

  // ── Load charts (year-wide) ──────────────────────────────
  useEffect(() => {
    setChartsLoading(true);
    Promise.all([
      attendanceAPI.getTeacherWise({ academicYearId: yearId }),
      attendanceAPI.getMonthlyTrend({ academicYearId: yearId }),
    ]).then(([t, tr]) => {
      setTeachers(t.data);
      setTrend((tr.data || []).map(r => ({
        month: r.month?.substring(0, 3), present: r.present, absent: r.absent, late: r.late, pct: r.pct,
      })));
    }).catch(() => {}).finally(() => setChartsLoading(false));
  }, [yearId, refreshKey]);

  // ── Load attendance records ──────────────────────────────
  const loadAttRecords = useCallback(async (search = attSearch) => {
    setAttLoading(true);
    const [startDate, endDate] = getDateRange(attDateMode, attCustomStart, attCustomEnd);
    try {
      const r = await attendanceAPI.getDailyRecords({
        academicYearId: yearId,
        startDate, endDate,
        ...(attStatus && { status: attStatus }),
        ...(search && { search }),
      });
      setAttRecords(r.data || []);
    } catch {}
    finally { setAttLoading(false); }
  }, [yearId, attDateMode, attCustomStart, attCustomEnd, attStatus, attSearch, getDateRange]); // eslint-disable-line

  useEffect(() => {
    if (moduleTab === 'Attendance') loadAttRecords();
  }, [moduleTab, yearId, attDateMode, attCustomStart, attCustomEnd, attStatus]); // eslint-disable-line

  // ── Stat card click → drill down ─────────────────────────
  const handleStatClick = status => setDrillStatus(prev => prev === status ? null : status);

  // ── Derived values ───────────────────────────────────────
  // present_count in getStats includes late; show only on-time for the Present card
  const onTimeCount = (todayStats?.present_count || 0) - (todayStats?.late_count || 0);

  const drillLabel = STATUS_CARDS.find(s => s.key === drillStatus)?.label || '';

  const pieData = todayStats ? [
    { name: 'Present',      value: Math.max(0, onTimeCount)                },
    { name: 'Late',         value: todayStats.late_count || 0              },
    { name: 'Absent',       value: todayStats.absent_count || 0            },
    { name: 'Half Day',     value: todayStats.half_day_count || 0          },
    { name: 'Single Punch', value: todayStats.single_punch_count || 0      },
  ].filter(d => d.value > 0) : [];

  const top5Data    = teachers.top5?.map(t => ({ name: t.name.split(' ')[0], attendance_pct: t.attendance_pct })) || [];
  const bottom5Data = teachers.bottom5?.map(t => ({ name: t.name.split(' ')[0], attendance_pct: t.attendance_pct })) || [];

  // ── Column defs ──────────────────────────────────────────
  const dailyCols = [
    { key: 'date',          label: 'Date',       sortable: true, render: fmtDate },
    { key: 'employee_code', label: 'Code',       sortable: true },
    { key: 'employee_name', label: 'Employee',   sortable: true },
    { key: 'department',    label: 'Department', sortable: true },
    { key: 'status',        label: 'Status',     sortable: true, render: v => <StatusBadge status={v}/> },
    { key: 'in_time',       label: 'In Time',    sortable: true, render: v => v || '—' },
    { key: 'out_time',      label: 'Out Time',   sortable: true, render: v => v || '—' },
  ];

  const drillCols = [
    { key: 'employee_code', label: 'Code',       sortable: true },
    { key: 'employee_name', label: 'Employee',   sortable: true },
    { key: 'department',    label: 'Department', sortable: true },
    { key: 'in_time',       label: 'In Time',    sortable: true, render: v => v || '—' },
    { key: 'out_time',      label: 'Out Time',   sortable: true, render: v => v || '—' },
  ];

  // ── Export ───────────────────────────────────────────────
  const handleExport = async () => {
    const [startDate, endDate] = getDateRange(attDateMode, attCustomStart, attCustomEnd);
    try {
      const r = await attendanceAPI.export({
        academicYearId: yearId, startDate, endDate,
        ...(attStatus && { status: attStatus }),
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'attendance.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  // ── Data freshness ───────────────────────────────────────
  const stalenessAlert = todayStats?.latest_date && (() => {
    const isStale = todayStats.latest_date < today;
    return (
      <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg border ${
        isStale ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
      }`}>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isStale ? 'bg-amber-400' : 'bg-emerald-400'}`}/>
        {isStale
          ? <>Data last updated <strong>{fmtDate(todayStats.latest_date)}</strong>. Today's attendance not yet synced — go to <button onClick={() => setModuleTab('Setup')} className="underline font-semibold hover:no-underline">Setup → Biometric Sync</button>.</>
          : <>Attendance data is up to date through <strong>{fmtDate(todayStats.latest_date)}</strong>.</>
        }
      </div>
    );
  })();

  // ══════════════════════════════════════════════════════════
  return (
    <div className="space-y-6 animate-fade-in">

      {/* Page header + tabs */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-bold text-slate-800">Employee Attendance</h2>
          <p className="text-slate-500 text-sm mt-0.5">Track and manage employee attendance</p>
        </div>
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-card border border-slate-100 shrink-0">
          {MODULE_TABS.map(({ key, Icon }) => (
            <button key={key} onClick={() => setModuleTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                moduleTab === key ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'
              }`}>
              <Icon size={15}/> {key}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════ ANALYTICS TAB ══════════════ */}
      {moduleTab === 'Analytics' && (
        <AttendanceAnalytics yearId={yearId} />
      )}

      {/* ══════════════ DASHBOARD TAB ══════════════ */}
      {moduleTab === 'Dashboard' && (
        <div className="space-y-6">

          {/* Enhanced period-based KPI dashboard */}
          <EnhancedDashboard yearId={yearId} />

          <div className="border-t border-slate-200 pt-2" />

          {/* Today's summary label */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Today's Summary</span>
            <span className="text-xs font-medium text-slate-400">· {fmtDate(today)}</span>
            {todayLoading && <Loader2 size={12} className="animate-spin text-primary-400"/>}
            <span className="ml-2 text-[11px] text-slate-400">Click a card to view those employees</span>
          </div>

          {/* Today's stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              title="Present" subtitle="on time"
              value={todayLoading ? null : onTimeCount}
              icon={UserCheck} color="green" loading={todayLoading}
              onClick={() => handleStatClick('present')} selected={drillStatus === 'present'}
            />
            <StatCard
              title="Absent"
              value={todayLoading ? null : (todayStats?.absent_count ?? '—')}
              icon={UserX} color="red" loading={todayLoading}
              onClick={() => handleStatClick('absent')} selected={drillStatus === 'absent'}
            />
            <StatCard
              title="Late Coming"
              value={todayLoading ? null : (todayStats?.late_count ?? '—')}
              icon={Clock} color="yellow" loading={todayLoading}
              onClick={() => handleStatClick('late')} selected={drillStatus === 'late'}
            />
            <StatCard
              title="Half Day"
              value={todayLoading ? null : (todayStats?.half_day_count ?? '—')}
              icon={UserMinus} color="purple" loading={todayLoading}
              onClick={() => handleStatClick('half_day')} selected={drillStatus === 'half_day'}
            />
            <StatCard
              title="Single Punch"
              value={todayLoading ? null : (todayStats?.single_punch_count ?? '—')}
              icon={Fingerprint} color="teal" loading={todayLoading}
              onClick={() => handleStatClick('single_punch')} selected={drillStatus === 'single_punch'}
            />
          </div>

          {/* Drill-down table */}
          {drillStatus && (
            <div className="glass-card p-5 space-y-3 border border-primary-100 animate-fade-in">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-slate-700">
                    Today's <span className="capitalize">{drillLabel}</span> Employees
                  </h3>
                  <StatusBadge status={drillStatus}/>
                  {!drillLoading && (
                    <span className="text-xs text-slate-400">· {drillRecords.length} record{drillRecords.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <button onClick={() => setDrillStatus(null)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600">
                  <X size={14}/>
                </button>
              </div>
              <DataTable
                columns={drillCols}
                data={drillRecords}
                loading={drillLoading}
                emptyText={`No ${drillLabel.toLowerCase()} employees today`}
              />
            </div>
          )}

          {/* Data freshness */}
          {stalenessAlert}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {pieData.length > 0
              ? <PieChartComponent title="Today's Attendance Distribution" data={pieData} donut/>
              : <div className="glass-card p-6 flex items-center justify-center text-xs text-slate-400">
                  No attendance data for today yet — sync the biometric device
                </div>
            }
            <LineChartComponent
              title="Monthly Attendance %"
              data={trend} xKey="month"
              lines={[{ key: 'pct', label: 'Attendance %', color: '#0ea5e9' }]}
            />
            <BarChartComponent
              title="Present vs Absent (Monthly)"
              data={trend} xKey="month"
              bars={[
                { key: 'present', label: 'Present', color: '#10b981' },
                { key: 'absent',  label: 'Absent',  color: '#f43f5e' },
                { key: 'late',    label: 'Late',    color: '#f59e0b' },
              ]}
            />
            <BarChartComponent
              title="Top 5 Employees by Attendance"
              data={top5Data} xKey="name"
              bars={[{ key: 'attendance_pct', label: 'Attendance %', color: '#10b981' }]}
            />
          </div>

          {/* Bottom 5 */}
          {bottom5Data.length > 0 && (
            <HorizontalBarChart
              title="Employees Needing Attention (Lowest Attendance)"
              data={bottom5Data} dataKey="attendance_pct" labelKey="name"
            />
          )}
        </div>
      )}

      {/* ══════════════ ATTENDANCE TAB ══════════════ */}
      {moduleTab === 'Attendance' && (
        <div className="space-y-4">

          {/* Filter panel */}
          <div className="glass-card p-4 space-y-4">

            {/* Date presets */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-14 shrink-0">Date</span>
              <div className="flex gap-1.5 flex-wrap">
                {DATE_PRESETS.map(({ key, label }) => (
                  <button key={key} onClick={() => setAttDateMode(key)}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-colors ${
                      attDateMode === key
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              {attDateMode === 'custom' && (
                <div className="flex items-center gap-2 flex-wrap ml-1">
                  <input type="date" value={attCustomStart}
                    onChange={e => setAttCustomStart(e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-400"/>
                  <span className="text-xs text-slate-400">to</span>
                  <input type="date" value={attCustomEnd}
                    onChange={e => setAttCustomEnd(e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-400"/>
                  <button onClick={() => loadAttRecords()}
                    className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium">
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-14 shrink-0">Status</span>
              <div className="flex gap-1.5 flex-wrap">
                {ATT_STATUS_FILTERS.map(({ key, label }) => (
                  <button key={key} onClick={() => setAttStatus(key)}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-colors ${
                      attStatus === key
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search + export */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                <input
                  type="text"
                  placeholder="Search by employee name or code…"
                  value={attSearchInput}
                  onChange={e => setAttSearchInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { setAttSearch(attSearchInput); loadAttRecords(attSearchInput); }
                    if (e.key === 'Escape') { setAttSearchInput(''); setAttSearch(''); loadAttRecords(''); }
                  }}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                {attSearchInput && (
                  <button onClick={() => { setAttSearchInput(''); setAttSearch(''); loadAttRecords(''); }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X size={13}/>
                  </button>
                )}
              </div>
              <button
                onClick={() => { setAttSearch(attSearchInput); loadAttRecords(attSearchInput); }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                <Search size={14}/> Search
              </button>
              <button onClick={handleExport}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors ml-auto">
                <Download size={14}/> Export
              </button>
            </div>
          </div>

          {/* Active filter chips + record count */}
          <div className="flex items-center gap-2 flex-wrap min-h-[24px]">
            {attLoading
              ? <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={11} className="animate-spin"/> Loading…</span>
              : <span className="text-xs font-medium text-slate-600">{attRecords.length.toLocaleString()} record{attRecords.length !== 1 ? 's' : ''}</span>
            }
            {attStatus && (
              <span className="inline-flex items-center gap-1 text-xs bg-primary-50 text-primary-700 border border-primary-200 px-2 py-0.5 rounded-full">
                <StatusBadge status={attStatus}/>
                <button onClick={() => setAttStatus('')} className="ml-0.5 text-primary-400 hover:text-primary-800 font-bold leading-none">×</button>
              </span>
            )}
            {attSearch && (
              <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-full">
                🔍 "{attSearch}"
                <button onClick={() => { setAttSearchInput(''); setAttSearch(''); loadAttRecords(''); }}
                  className="ml-0.5 text-slate-400 hover:text-slate-700 font-bold leading-none">×</button>
              </span>
            )}
            {attRecords.length >= 2000 && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                Showing first 2,000 — narrow the date range for full results
              </span>
            )}
          </div>

          {/* Records table */}
          <DataTable
            columns={dailyCols}
            data={attRecords}
            loading={attLoading}
            emptyText="No records found. Adjust the date range, status filter, or sync the biometric device."
          />
        </div>
      )}

      {/* ══════════════ SETUP TAB ══════════════ */}
      {moduleTab === 'Setup' && (
        <div className="space-y-6">

          {/* Import / Export */}
          <div className="glass-card p-5 space-y-4">
            <div>
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Upload size={16} className="text-primary-600"/> Import / Export Attendance
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Upload an Excel file to bulk-import records, or export current data</p>
            </div>
            <ImportExport
              module="Attendance"
              onImport={fd => attendanceAPI.import(fd, { academicYearId: yearId })}
              onExport={() => attendanceAPI.export({ academicYearId: yearId })}
              onSampleTemplate={attendanceAPI.sampleTemplate}
              onSuccess={loadTodayStats}
            />
          </div>

          {/* Biometric Sync */}
          <div className="space-y-2">
            <div>
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Fingerprint size={16} className="text-primary-600"/> Biometric Device Sync
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Manage biometric devices and configure automatic attendance sync</p>
            </div>
            <BiometricSync embedded/>
          </div>

        </div>
      )}

    </div>
  );
}
